import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Countdown from 'react-countdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SpinnerLoading from '../../components/pageComponents/SpinnerLoading';
import ModalMensaje from '../../components/modalMensaje/ModalMensaje';
import logoInnova from '../../image/logo_menu.svg';
import imgInicio from '../../image/Img_Inicio.jpg';
import {
  validarTokenPostulanteAPI,
  getEvaluacionPublicaAPI,
  guardarRespuestaEvaluacionAPI,
  iniciarEvaluacionTokenAPI,
  estadosEvaluacionTokenAPI,
} from '../../consumers/backendApisUrls';
import { responseCode200 } from '../../consumers/httpRequiestsUtils';

const IMAGE_LOCK = "https://intranet.innovaschools.edu.pe/Content/image/lock_1.png";
const MENSAJE_EXPIRADO = "El enlace de acceso ha caducado o no es válido. Por favor contacta al área de Recursos Humanos.";

// Mapa de tipos de evaluación
const TIPO_EVALUACION = {
  248801: { label: 'Psicológica',   color: 'bg-purple',  hex: '#6f42c1' },
  248802: { label: 'Conocimientos', color: 'bg-info',    hex: '#0dcaf0' },
  248803: { label: 'Otros',         color: 'bg-warning', hex: '#ffc107' },
};
const getTipoEval = (id) => TIPO_EVALUACION[id] ?? { label: 'Tipo desc.', hex: '#6c757d' };

// Fases del flujo
const FASE = {
  CARGANDO:  'CARGANDO',   // Fase 1 – validando token / cargando datos
  LISTA:     'LISTA',      // Fase 2 – listado de evaluaciones asignadas
  INICIANDO: 'INICIANDO',  // Transición – iniciando evaluación + cargando preguntas
  EVALUACION:'EVALUACION', // Fase 3 – respondiendo preguntas
  GUARDANDO: 'GUARDANDO',  // Transición – guardando respuestas y finalizando
  COMPLETADO:'COMPLETADO', // Fase 4 – todas las evaluaciones terminadas
};

// ── Helpers de peticiones públicas (sin sesión de empleado) ──────────────────

const buildAuthHeaders = (tokenValue) => ({
  'content-type': 'application/json',
  'Access-Control-Allow-Origin': '*',
  'Access-Control-Allow-Methods': 'GET, POST',
  Authorization: 'Bearer ' + tokenValue,
});

const executeGetPublic = (api, tokenValue) => {
  const base = process.env.REACT_APP_URL_API_BACKEND;
  if (!api) return Promise.resolve({ status: 0 });
  return axios
    .get(`${base}${api}`, { headers: buildAuthHeaders(tokenValue) })
    .catch(err => err.response ?? { status: 0 });
};

const executePostPublic = (api, body, tokenValue) => {
  const base = process.env.REACT_APP_URL_API_BACKEND;
  if (!api) return Promise.resolve({ status: 0 });
  return axios
    .post(`${base}${api}`, body, { headers: buildAuthHeaders(tokenValue) })
    .catch(err => err.response ?? { status: 0 });
};

const toQuery = (obj = {}) => new URLSearchParams(
  Object.entries(obj)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => [key, String(value)])
).toString();

const parseJsonIfString = (value) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const unwrapEnvelope = (input) => {
  let current = input;
  for (let i = 0; i < 6; i += 1) {
    current = parseJsonIfString(current);
    if (!current || typeof current !== 'object') return current;

    const nested =
      current.Data ??
      current.data ??
      current.Body ??
      current.body ??
      current.Result ??
      current.result ??
      current.Payload ??
      current.payload;

    if (nested === undefined) return current;
    current = nested;
  }
  return current;
};

const findFirstNumericDeep = (value, keys, maxDepth = 6) => {
  const queue = [{ node: value, depth: 0 }];
  const normalizedKeys = keys.map((key) => key.toLowerCase());

  while (queue.length > 0) {
    const { node, depth } = queue.shift();
    if (!node || depth > maxDepth) continue;

    const current = parseJsonIfString(node);
    if (!current) continue;

    if (Array.isArray(current)) {
      current.forEach((item) => queue.push({ node: item, depth: depth + 1 }));
      continue;
    }

    if (typeof current !== 'object') continue;

    for (const [key, raw] of Object.entries(current)) {
      const keyLower = key.toLowerCase();
      if (normalizedKeys.includes(keyLower)) {
        const parsed = Number(raw);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
      }
      if (raw && (typeof raw === 'object' || typeof raw === 'string')) {
        queue.push({ node: raw, depth: depth + 1 });
      }
    }
  }

  return 0;
};

const decodeJwtPayload = (tokenValue) => {
  try {
    if (!tokenValue || typeof tokenValue !== 'string') return null;
    const parts = tokenValue.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

/**
 * Extrae el payload real de la respuesta anidada del backend.
 * Estructura típica: response.data.data.Data  (axios → envelope → payload)
 */
const extractPayload = (response) => {
  const envelope = response?.data?.data ?? response?.data;
  return unwrapEnvelope(envelope);
};

const extractValidarTokenData = (response) => {
  const root = response?.data?.data ?? response?.data ?? {};
  const nuevoToken = root?.nuevoToken ?? root?.NuevoToken ?? null;

  const postulanteEnvelope = root?.postulante ?? root?.Postulante ?? root;
  const postulanteData = unwrapEnvelope(postulanteEnvelope);

  return {
    nuevoToken,
    postulanteData,
  };
};

const getEvaluacionesIds = (payload) => {
  const raw =
    payload?.evaluaciones ??
    payload?.Evaluaciones ??
    payload?.postulacionEvaluaciones ??
    payload?.PostulacionEvaluaciones ??
    payload?.idsEvaluaciones ??
    payload?.IdsEvaluaciones ??
    payload?.evaluacionIds ??
    payload?.EvaluacionIds;

  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === 'number' || typeof item === 'string') return Number(item);
        if (item && typeof item === 'object') {
          return Number(
            item.idEvaluacion ??
            item.IdEvaluacion ??
            item.id ??
            item.Id
          );
        }
        return NaN;
      })
      .filter((value) => Number.isFinite(value) && value > 0);
  }

  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((item) => Number(item.trim()))
      .filter((value) => Number.isFinite(value) && value > 0);
  }

  return [];
};

const getPostulacionId = (payload, tokenValue) => {
  const fromPayload = findFirstNumericDeep(payload, ['idPostulacion', 'IdPostulacion']);
  if (fromPayload > 0) return fromPayload;

  const jwtPayload = decodeJwtPayload(tokenValue);
  return findFirstNumericDeep(jwtPayload, ['idPostulacion', 'IdPostulacion']);
};

const getPostulanteId = (payload, tokenValue) => {
  const fromPayload = findFirstNumericDeep(payload, ['idPostulante', 'IdPostulante']);
  if (fromPayload > 0) return fromPayload;

  const jwtPayload = decodeJwtPayload(tokenValue);
  return findFirstNumericDeep(jwtPayload, ['idPostulante', 'IdPostulante', 'nameid', 'sub', 'unique_name', 'SessionKeyEntityId', 'SesionKeyEntityId']);
};

const resolvePostulanteId = (payload, tokenValue, preferredId = 0) => {
  if (Number(preferredId) > 0) return Number(preferredId);

  const fromPayload = getPostulanteId(payload, tokenValue);
  if (fromPayload > 0) return fromPayload;

  const fromPostulacion = getPostulacionId(payload, tokenValue);
  if (fromPostulacion > 0) return fromPostulacion;

  const jwtPayload = decodeJwtPayload(tokenValue) || {};
  const fromJwt = Number(
    jwtPayload?.unique_name ??
    jwtPayload?.SessionKeyEntityId ??
    jwtPayload?.SesionKeyEntityId ??
    jwtPayload?.nameid ??
    jwtPayload?.sub ??
    0
  );

  return Number.isFinite(fromJwt) && fromJwt > 0 ? fromJwt : 0;
};

const getIdsFromEstadosPayload = (payload) => {
  const lista = Array.isArray(payload)
    ? payload
    : (Array.isArray(payload?.items) ? payload.items : (Array.isArray(payload?.Items) ? payload.Items : []));

  return lista
    .map((item) => Number(item?.idEvaluacion ?? item?.IdEvaluacion ?? item?.id ?? item?.Id))
    .filter((value) => Number.isFinite(value) && value > 0);
};

const normalizeEstadosMap = (payload) => {
  const lista = Array.isArray(payload)
    ? payload
    : (Array.isArray(payload?.items) ? payload.items : (Array.isArray(payload?.Items) ? payload.Items : []));

  const map = new Map();

  lista.forEach((item) => {
    if (!item || typeof item !== 'object') return;

    const idEvaluacion = Number(item?.idEvaluacion ?? item?.IdEvaluacion ?? item?.id ?? item?.Id);
    if (!Number.isFinite(idEvaluacion) || idEvaluacion <= 0) return;

    const estadoTexto =
      item?.estadoEvaluacion ??
      item?.EstadoEvaluacion ??
      item?.estado ??
      item?.Estado ??
      item?.descripcionEstado ??
      item?.DescripcionEstado ??
      item?.nombreEstado ??
      item?.NombreEstado ??
      null;

    const estadoTextoNormalized =
      typeof estadoTexto === 'string' && estadoTexto.trim().length > 0
        ? estadoTexto.trim()
        : null;

    const completada =
      item?.completada === true ||
      item?.Completada === true ||
      item?.finalizada === true ||
      item?.Finalizada === true ||
      item?.esCompletada === true ||
      item?.EsCompletada === true ||
      item?.fechaFinalizacion !== null ||
      item?.FechaFinalizacion !== null ||
      (estadoTextoNormalized ? /(completad|finalizad|terminad|lista)/i.test(estadoTextoNormalized) : false);

    map.set(idEvaluacion, {
      estadoLabel: estadoTextoNormalized ?? (completada ? 'Completada' : 'Pendiente'),
      completadaBackend: completada,
    });
  });

  return map;
};

const isFinalizadaLabel = (label) => {
  if (typeof label !== 'string') return false;
  return /finalizad/i.test(label);
};

const applyEstadosToEvaluaciones = (evaluaciones, estadosMap) => {
  if (!(estadosMap instanceof Map) || estadosMap.size === 0) {
    return evaluaciones.map((ev) => ({
      ...ev,
      estadoLabel: ev.estadoLabel ?? 'Pendiente',
      completadaBackend: ev.completadaBackend ?? false,
    }));
  }

  return evaluaciones.map((ev) => {
    const estado = estadosMap.get(ev.idEvaluacion);
    if (!estado) {
      return {
        ...ev,
        estadoLabel: ev.estadoLabel ?? 'Pendiente',
        completadaBackend: ev.completadaBackend ?? false,
      };
    }

    return {
      ...ev,
      estadoLabel: estado.estadoLabel ?? ev.estadoLabel ?? 'Pendiente',
      completadaBackend: estado.completadaBackend,
    };
  });
};

const normalizeEvaluacionItems = (payload) => {
  if (!payload) return [];

  const parseOne = (item, idx) => {
    if (!item || typeof item !== 'object') return null;
    const idEvaluacion = Number(item?.idEvaluacion ?? item?.IdEvaluacion ?? item?.id ?? item?.Id ?? 0);
    if (!Number.isFinite(idEvaluacion) || idEvaluacion <= 0) return null;

    const preguntas = Array.isArray(item?.preguntas) ? item.preguntas : [];
    return {
      idEvaluacion,
      nombre: item?.nombreEvaluacion ?? item?.nombre ?? item?.nombre_evaluacion ?? `Evaluación ${idx + 1}`,
      preguntas,
      instrucciones: item?.instrucciones ?? null,
      tiempoLimite: item?.tiempoLimite ?? null,
      idTipoEvaluacion: item?.idTipoEvaluacion ?? null,
      estadoLabel: item?.estadoEvaluacion ?? item?.EstadoEvaluacion ?? item?.estado ?? item?.Estado ?? 'Pendiente',
      completadaBackend: Boolean(item?.completada ?? item?.Completada ?? item?.finalizada ?? item?.Finalizada),
      hayError: false,
    };
  };

  if (Array.isArray(payload)) {
    return payload.map(parseOne).filter(Boolean);
  }

  if (Array.isArray(payload?.items)) {
    return payload.items.map(parseOne).filter(Boolean);
  }

  if (Array.isArray(payload?.Items)) {
    return payload.Items.map(parseOne).filter(Boolean);
  }

  if (typeof payload === 'object') {
    const one = parseOne(payload, 0);
    return one ? [one] : [];
  }

  return [];
};

const getEstadosEvaluacion = async (idPostulante, tokenValue) => {
  if (!(Number(idPostulante) > 0)) return { status: 0 };
  return executePostPublic(
    estadosEvaluacionTokenAPI(),
    { IdPostulante: Number(idPostulante) },
    tokenValue,
  );
};

// ── Componente principal ─────────────────────────────────────────────────────

const EvaluacionesPostulante = () => {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token');

  const [fase, setFase]                     = useState(FASE.CARGANDO);
  const [lockInfo, setLockInfo]             = useState(null);
  const [postulanteData, setPostulanteData] = useState(null);
  // Fase 2 – lista de evaluaciones: [{ idPostulacionEvaluacion, idEvaluacion, nombre, ... }]
  const [evaluacionesList, setEvaluacionesList] = useState([]);
  const [currentEvalIdx, setCurrentEvalIdx] = useState(0);
  // Fase 3 – preguntas de la evaluación en curso
  const [preguntasList, setPreguntasList]   = useState([]);
  const [currentPregIdx, setCurrentPregIdx] = useState(0);
  // { [idPregunta]: { idOpcion: number|null, respuestaTexto: string|null } }
  const [respuestas, setRespuestas]         = useState({});
  const [errorMsg, setErrorMsg]             = useState(null);
  const [showNavWarning, setShowNavWarning] = useState(false);
  const beforeUnloadRef = useRef(null);
  const popstateRef     = useRef(null);
  const fechaInicioRef  = useRef(null);   // ISO string: when current eval was started
  const authTokenRef    = useRef(token);
  const idPostulanteRef = useRef(0);
  const finalizadoRef   = useRef(false);  // evita doble finalización
  const beaconRef       = useRef(false);  // controla envío beacon en unload/hidden

  useEffect(() => {
    if (!token) {
      setLockInfo({ message: 'Token de acceso no válido o ausente.' });
      setFase(null);
      return;
    }
    validarAcceso(token);
    const script = document.createElement("script");
    script.src = "https://itc-client-analytic.web.app/itc_hubai_embed.js?key=1f1dc451-b31b-4b7e-82d0-5b531f94f6f1";
    script.async = true;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Navigation guard: warn on reload / back when timer is running ─────────
  useEffect(() => {
    const tiempoLimite = evaluacionesList[currentEvalIdx]?.tiempoLimite ?? 0;
    const timerActivo  = fase === FASE.EVALUACION && tiempoLimite > 0;
    if (!timerActivo) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    const handlePopstate = () => {
      window.history.pushState(null, '');
      setShowNavWarning(true);
    };

    beforeUnloadRef.current = handleBeforeUnload;
    popstateRef.current     = handlePopstate;

    window.history.pushState(null, '');
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopstate);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopstate);
    };
  }, [fase, currentEvalIdx, evaluacionesList]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── FASE 1: Validar token y obtener datos del postulante ──────────────────

  const validarAcceso = async (tokenValue) => {
    setFase(FASE.CARGANDO);
    try {
      const res = await executeGetPublic(validarTokenPostulanteAPI(), tokenValue);
      console.log('[Fase1] ValidarToken status:', res?.status);
      if (!responseCode200(res)) {
        setLockInfo({ message: MENSAJE_EXPIRADO });
        return;
      }
      const { nuevoToken, postulanteData } = extractValidarTokenData(res);
      const tokenVigente = nuevoToken || tokenValue;
      authTokenRef.current = tokenVigente;
      const data = postulanteData;
      console.log('[Fase1] postulanteData:', data);
      setPostulanteData(data);

      // Acepta distintos formatos de payload para evitar quedar en estado vacío por casing.
      const idPostulacion = getPostulacionId(data, tokenVigente);
      const idPostulante = getPostulanteId(data, tokenVigente);
      idPostulanteRef.current = idPostulante > 0 ? idPostulante : idPostulacion;
      const idPostulanteForEstados = idPostulante > 0 ? idPostulante : idPostulacion;
      console.log('[Fase1] ids detectados:', { idPostulacion, idPostulante });
      let evalIds = getEvaluacionesIds(data);
      let estadosMap = new Map();

      console.log('[Fase1] evalIds desde ValidarToken:', evalIds);

      if (idPostulanteForEstados > 0) {
        try {
          const estadosRes = await getEstadosEvaluacion(idPostulanteForEstados, tokenVigente);
          const estadosPayload = extractPayload(estadosRes);
          const idsDesdeEstados = getIdsFromEstadosPayload(estadosPayload);
          estadosMap = normalizeEstadosMap(estadosPayload);
          if (evalIds.length === 0 && idsDesdeEstados.length > 0) {
            evalIds = idsDesdeEstados;
          }
          console.log('[Fase1] evalIds desde Evaluaciones/EstadoEvaluacion:', idsDesdeEstados);
          console.log('[Fase1] estados obtenidos:', estadosMap.size);
        } catch (estadosError) {
          console.warn('[Fase1] No se pudieron recuperar estados por Evaluaciones/EstadoEvaluacion:', estadosError);
        }
      }

      console.log('[Fase1] evalIds finales:', evalIds);

      // ── FASE 2: Recuperar nombres vía PostulacionEvaluaciones/GetByPostulacion
      //           luego preguntas vía Evaluaciones/GetPreguntas (ambos con token postulante)
      await cargarEvaluaciones(idPostulacion, evalIds, tokenVigente, estadosMap);
    } catch (err) {
      console.error('[Fase1] Error:', err);
      setLockInfo({ message: MENSAJE_EXPIRADO });
    }
  };

  // ── FASE 2: Nombres y preguntas via GET Evaluaciones/GetEvaluacion?idEvaluacion={id}

  const cargarEvaluaciones = async (idPostulacion, evalIds, tokenValue, estadosMap = new Map()) => {
    try {
      if (evalIds.length === 0) {
        console.warn('[Fase2] No hay evaluaciones asignadas');
        setFase(FASE.LISTA);
        return;
      }

      // GET Evaluacion/GetEvaluacion?idEvaluacion={id} en paralelo para todos los IDs
      // (ruta singular → PostulanteAPI Lambda, sin auth de empleado)
      const evalResArr = await Promise.all(
        evalIds.map(id => executeGetPublic(getEvaluacionPublicaAPI(id), tokenValue))
      );

      const lista = evalIds.map((idEvaluacion, idx) => {
        const res = evalResArr[idx];
        console.log(`[Fase2] GetEvaluacion id=${idEvaluacion} status:`, res?.status, 'data:', res?.data);
        if (res?.status === 500) {
          console.error(`[Fase2] Error 500 al obtener evaluación id=${idEvaluacion} — el backend falló internamente`);
        }
        const payload       = extractPayload(res);
        const nombre        = payload?.nombreEvaluacion ?? payload?.nombre ?? payload?.nombre_evaluacion ?? `Evaluación ${idx + 1}`;
        // Campos reales: preguntas[].idEvaluacionPregunta, .textoPregunta, .opciones[].idOpcionRespuesta, .textoOpcion
        const preguntas     = Array.isArray(payload?.preguntas) ? payload.preguntas : [];
        const instrucciones   = payload?.instrucciones   ?? null;
        const tiempoLimite    = payload?.tiempoLimite    ?? null;
        const idTipoEvaluacion = payload?.idTipoEvaluacion ?? null;
        const hayError        = !payload || res?.status >= 400;
        console.log(`[Fase2] id=${idEvaluacion} nombre="${nombre}" tipo=${idTipoEvaluacion} preguntas=${preguntas.length} hayError=${hayError}`);
        return { idEvaluacion, nombre, preguntas, instrucciones, tiempoLimite, idTipoEvaluacion, hayError };
      });

      const listaConEstados = applyEstadosToEvaluaciones(lista, estadosMap);

      const todosConError = listaConEstados.every(e => e.hayError);
      if (todosConError) {
        setErrorMsg('Error al cargar evaluaciones.');
      }

      setEvaluacionesList(listaConEstados);

      const firstPendingIdx = listaConEstados.findIndex((item) => !isFinalizadaLabel(item.estadoLabel));
      if (firstPendingIdx >= 0) {
        setCurrentEvalIdx(firstPendingIdx);
      }
    } catch (err) {
      console.error('[Fase2] Error:', err);
    } finally {
      setFase(FASE.LISTA);
    }
  };

  // ── FASE 3: Iniciar la primera evaluación ─────────────────────────────────
  //   Las preguntas ya están cargadas en evaluacionesList[idx].preguntas

  const notificarInicioEvaluacion = async (idPostulante, idEvaluacion, tokenValue) => {
    const startBody = { IdPostulante: idPostulante, IdEvaluacion: idEvaluacion };
    const estadosBody = { IdPostulante: idPostulante };

    // Endpoint correcto: POST /Evaluaciones/IniciarEvaluacion
    console.log('[Fase3] POST /Evaluaciones/IniciarEvaluacion body:', startBody);
    const startResponse = await executePostPublic(iniciarEvaluacionTokenAPI(), startBody, tokenValue);
    console.log('[Fase3] IniciarEvaluacion status:', startResponse?.status);
    if (!responseCode200(startResponse)) {
      console.warn('[Fase3] IniciarEvaluacion no respondió 200:', startResponse?.status);
    }

    console.log('[Fase3] POST /Evaluaciones/EstadoEvaluacion body:', estadosBody);
    const estadosResponse = await executePostPublic(estadosEvaluacionTokenAPI(), estadosBody, tokenValue);
    console.log('[Fase3] Estados status (post-inicio):', estadosResponse?.status);
    if (!responseCode200(estadosResponse)) {
      console.warn('[Fase3] Estados no respondió 200:', estadosResponse?.status);
    }
  };

  const iniciarEvaluacion = async () => {
    const evalActual = evaluacionesList[currentEvalIdx];
    if (!evalActual) return;
    const tokenActivo = authTokenRef.current || token;
    const idPostulante = resolvePostulanteId(postulanteData, tokenActivo, idPostulanteRef.current);

    if (idPostulante > 0) {
      idPostulanteRef.current = idPostulante;
    }

    setFase(FASE.INICIANDO);
    if (evalActual?.idEvaluacion) {
      try {
        if (idPostulante > 0) {
          console.log('[Fase3] POST IniciarEvaluacion payload:', { IdPostulante: idPostulante, IdEvaluacion: evalActual.idEvaluacion });
          await notificarInicioEvaluacion(idPostulante, evalActual.idEvaluacion, tokenActivo);
        } else {
          console.warn('[Fase3] No se pudo resolver IdPostulante para POST IniciarEvaluacion');
        }
      } catch (error) {
        console.error('[Fase3] Error notificando inicio de evaluación:', error);
      }
    } else {
      console.warn('[Fase3] No se pudo invocar IniciarEvaluacion por idEvaluacion inválido:', { idPostulante, idEvaluacion: evalActual?.idEvaluacion });
    }

    console.log('[Fase3] Iniciando evaluación:', evalActual.nombre, '- preguntas:', evalActual.preguntas.length);
    fechaInicioRef.current = new Date().toISOString();
    finalizadoRef.current = false;
    beaconRef.current = false;
    setPreguntasList(evalActual.preguntas);
    setRespuestas({});
    setCurrentPregIdx(0);
    setFase(FASE.EVALUACION);
  };

  // ── FASE 3c: Registrar respuesta de la pregunta actual ───────────────────

  const responderPregunta = (idPregunta, value, esTexto) => {
    setRespuestas(prev => ({
      ...prev,
      [idPregunta]: esTexto
        ? { idOpcion: null,  respuestaTexto: value }
        : { idOpcion: value, respuestaTexto: null  },
    }));
  };

  // ── FASE 3d: Finalizar evaluación actual y pasar a la siguiente ──────────

  const finalizarEvaluacion = async () => {
    const evalActual     = evaluacionesList[currentEvalIdx];
    const fechaFin       = new Date().toISOString();
    const idPostulacion  = postulanteData?.idPostulacion;
    const idEvaluacion   = evalActual?.idEvaluacion;

    // Construir lista de respuestas para el SP — se incluyen TODAS las preguntas
    // (aunque no hayan sido respondidas) para que el SP registre el intento completo.
    const respuestasPayload = (evalActual?.preguntas ?? []).map(preg => ({
      idEvaluacionPregunta: preg.idEvaluacionPregunta,
      idOpcionRespuesta:    respuestas[preg.idEvaluacionPregunta]?.idOpcion ?? null,
    }));

    if (idPostulacion && idEvaluacion) {
      setFase(FASE.GUARDANDO);
      try {
        await executePostPublic(
          guardarRespuestaEvaluacionAPI(),
          {
            idPostulacion,
            idEvaluacion,
            fechaInicio:       fechaInicioRef.current,
            fechaFinalizacion: fechaFin,
            respuestas:        respuestasPayload,
          },
          authTokenRef.current || token,
        );

        setEvaluacionesList((prev) =>
          prev.map((item, idx) =>
            idx === currentEvalIdx
              ? { ...item, completadaBackend: true, estadoLabel: 'Completada' }
              : item
          )
        );
      } catch (err) {
        console.error('[finalizarEvaluacion] Error al guardar respuestas:', err);
      }
    }

    const siguiente = currentEvalIdx + 1;
    console.log('[Fase3d] Evaluación completada. Siguiente idx:', siguiente, '/ total:', evaluacionesList.length);
    if (siguiente >= evaluacionesList.length) {
      setFase(FASE.COMPLETADO);
    } else {
      setCurrentEvalIdx(siguiente);
      fechaInicioRef.current = null;
      setPreguntasList([]);
      setRespuestas({});
      setCurrentPregIdx(0);
      setFase(FASE.LISTA);
    }
  };

  // ── Renders ───────────────────────────────────────────────────────────────

  const confirmarSalida = () => {
    setShowNavWarning(false);
    if (beforeUnloadRef.current) window.removeEventListener('beforeunload', beforeUnloadRef.current);
    if (popstateRef.current)     window.removeEventListener('popstate',     popstateRef.current);
    window.history.go(-2);
  };

  if (lockInfo) return (
    <ModalMensaje
      modalMensaje={lockInfo.message}
      modalImage={IMAGE_LOCK}
      styleFicha={{ color: 'red' }}
      hasBackButton={false}
      toggleModal={() => {}}
    />
  );

  if (fase === FASE.CARGANDO || fase === FASE.GUARDANDO || fase === FASE.INICIANDO) return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <div className="text-center">
        <SpinnerLoading />
        <p className="mt-3 text-muted">
          {fase === FASE.GUARDANDO
            ? 'Guardando respuestas…'
            : (fase === FASE.INICIANDO ? 'Iniciando evaluación…' : 'Verificando credenciales…')}
        </p>
      </div>
    </div>
  );

  const evalActual     = evaluacionesList[currentEvalIdx];
  const progressPct    = evaluacionesList.length > 0
    ? Math.round((currentEvalIdx / evaluacionesList.length) * 100)
    : 0;

  const fondoCompletado = fase === FASE.COMPLETADO
    ? {
        backgroundImage: `url(${imgInicio})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }
    : {};

  return (
    <div className="bg-light min-vh-100 py-5" style={fondoCompletado}>
      {showNavWarning && (
        <ModalAdvertenciaNavegacion
          onCancelar={() => setShowNavWarning(false)}
          onConfirmar={confirmarSalida}
        />
      )}
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div
              className="card shadow-lg border-0"
              style={fase === FASE.COMPLETADO ? { backgroundColor: 'rgba(255,255,255,0.88)' } : undefined}
            >

              {/* ── Header sticky ── */}
              <div style={{ position: 'sticky', top: 0, zIndex: 1040, backgroundColor: '#0d6efd' }}>
                <div className="card-header bg-primary text-white p-4 d-flex align-items-center justify-content-between gap-3" style={{ borderBottom: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>
                  <div>
                    <h3 className="mb-1 d-flex align-items-center">
                      <FontAwesomeIcon icon="clipboard-list" className="me-3" style={{ marginRight: '1rem' }} />
                      Proceso de Evaluación
                    </h3>
                    {postulanteData && (
                      <p className="mb-0 mt-1 opacity-75 small">
                        <strong>
                          {postulanteData.nombres} {postulanteData.apellidoPaterno} {postulanteData.apellidoMaterno}
                        </strong>
                        {' · '}{postulanteData.cargo}
                        {postulanteData.fechaLimiteEvaluacion && ` · Fecha límite: ${postulanteData.fechaLimiteEvaluacion}`}
                      </p>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-3 ms-3" style={{ whiteSpace: 'nowrap' }}>
                    <img src={logoInnova} alt="Innova Schools" height="52" style={{ objectFit: 'contain' }} />
                    <span className="fw-bold" style={{ letterSpacing: '0.05em', fontSize: '0.95rem' }}>INNOVA SCHOOLS</span>
                  </div>
                </div>

                {/* ── Barra de progreso global ── */}
                {evaluacionesList.length > 0 && (
                  <div className="progress rounded-0" style={{ height: '4px', backgroundColor: '#d0e3ff' }}>
                    <div
                      className="progress-bar"
                      style={{
                        width: `${progressPct}%`,
                        transition: 'width 0.4s',
                        backgroundColor: '#28c76f',
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="card-body p-4 p-lg-5">
                {errorMsg && <div className="alert alert-danger mb-4">{errorMsg}</div>}

                {/* ── FASE LISTA: Presentación + botón Iniciar ── */}
                {fase === FASE.LISTA && (
                  <FaseLista
                    evaluacionesList={evaluacionesList}
                    currentEvalIdx={currentEvalIdx}
                    onIniciar={iniciarEvaluacion}
                  />
                )}

                {/* ── FASE EVALUACION: Preguntas ── */}
                {fase === FASE.EVALUACION && (
                  <FaseEvaluacion
                    key={currentEvalIdx}
                    evalActual={evalActual}
                    totalEvals={evaluacionesList.length}
                    evalIdx={currentEvalIdx}
                    preguntasList={preguntasList}
                    respuestas={respuestas}
                    onResponder={responderPregunta}
                    onFinalizar={finalizarEvaluacion}
                    postulanteData={postulanteData}
                    fechaInicioRef={fechaInicioRef}
                    authTokenRef={authTokenRef}
                    tokenValue={token}
                    fase={fase}
                    finalizadoRef={finalizadoRef}
                    beaconRef={beaconRef}
                  />
                )}

                {/* ── FASE COMPLETADO ── */}
                {fase === FASE.COMPLETADO && (
                  <div className="text-center py-5" style={{ padding: '80px 24px' }}>
                    <div className="mb-4 text-success">
                      <FontAwesomeIcon icon="check-circle" size="5x" />
                    </div>
                    <h3>¡Evaluaciones Completadas!</h3>
                    <p className="lead text-muted">
                      Gracias por completar todas sus evaluaciones asignadas.
                      <br />
                      El equipo de RRHH revisará sus resultados y se pondrá en contacto pronto.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Sub-componente: Fase 2 – Listado de evaluaciones (timeline horizontal) ───

const FaseLista = ({ evaluacionesList, currentEvalIdx, onIniciar }) => {
  if (evaluacionesList.length === 0) return (
    <div className="text-center py-5">
      <div className="alert alert-warning mb-0">
        No se encontraron evaluaciones asignadas para este postulante.
      </div>
    </div>
  );

  return (
    <div>
      <h5 className="mb-1">Evaluaciones asignadas</h5>
      {(() => {
        const pendientes = evaluacionesList.filter(ev => !isFinalizadaLabel(ev.estadoLabel)).length;
        const plural = pendientes === 1 ? '' : 'es';
        return (
          <p className="text-muted small mb-4">
            Tienes <strong>{pendientes}</strong> evaluación{plural} por completar.
          </p>
        );
      })()}

      {/* ── Timeline horizontal ─────────────────────────────────────────── */}
      <div className="position-relative mb-5 px-2">
        {/* Línea de conexión */}
        <div
          style={{
            position: 'absolute',
            top: 20,
            left: '5%',
            right: '5%',
            height: 3,
            background: '#dee2e6',
            zIndex: 0,
          }}
        />
        {/* Línea de progreso */}
        {currentEvalIdx > 0 && (
          <div
            style={{
              position: 'absolute',
              top: 20,
              left: '5%',
              width: `${Math.min(((currentEvalIdx) / (evaluacionesList.length - 1)) * 90, 90)}%`,
              height: 3,
              background: '#0d6efd',
              zIndex: 1,
              transition: 'width 0.4s',
            }}
          />
        )}

        {/* Nodos */}
        <div className="d-flex justify-content-between position-relative" style={{ zIndex: 2 }}>
          {evaluacionesList.map((ev, idx) => {
            const estadoTexto = ev.estadoLabel ?? (ev.completadaBackend ? 'Completada' : 'Pendiente');
            const estadoNorm = (estadoTexto || '').toLowerCase();
            const esNoIniciada = /no\s*iniciad/.test(estadoNorm);
            const esFinalizada = !esNoIniciada && (/finalizad|completad/.test(estadoNorm) || ev.completadaBackend === true);
            const showCheck = esFinalizada;
            const actual     = !esFinalizada && idx === currentEvalIdx;
            const pendiente  = !esFinalizada && !actual;
            return (
              <div
                key={ev.idEvaluacion}
                className="d-flex flex-column align-items-center"
                style={{ flex: 1, minWidth: 0 }}
              >
                {/* Círculo */}
                <div
                  className={`rounded-circle d-flex align-items-center justify-content-center border border-2 ${
                    esFinalizada ? '' :
                    esNoIniciada ? 'bg-white border-secondary text-secondary' :
                    actual       ? 'bg-primary border-primary text-white' :
                                   'bg-white border-secondary text-secondary'
                  }`}
                  style={{
                    width: 42,
                    height: 42,
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    boxShadow: actual ? '0 0 0 4px rgba(13,110,253,0.2)' : 'none',
                    backgroundColor: esFinalizada ? '#28c76f' : esNoIniciada ? '#f8f9fa' : undefined,
                    borderColor: esFinalizada ? '#28c76f' : esNoIniciada ? '#ced4da' : undefined,
                    color: esFinalizada ? '#fff' : undefined,
                  }}
                >
                  {showCheck
                    ? <FontAwesomeIcon icon="check" />
                    : <span>{idx + 1}</span>
                  }
                </div>

                {/* Nombre */}
                <div
                  className={`mt-2 text-center ${
                    esFinalizada ? 'text-muted' :
                    actual       ? 'text-primary fw-semibold' :
                                   'text-muted'
                  }`}
                  style={{ fontSize: '0.72rem', lineHeight: 1.3, maxWidth: 90, overflow: 'hidden' }}
                  title={ev.nombre}
                >
                  {ev.nombre.length > 28 ? ev.nombre.slice(0, 26) + '…' : ev.nombre}
                </div>

                {/* Badge estado */}
                <span
                  className="badge mt-1"
                  style={{
                    fontSize: '0.8rem',
                    backgroundColor: esFinalizada
                      ? '#28c76f'
                      : esNoIniciada
                        ? '#ced4da'
                        : '#ffca2c',
                    color: esFinalizada ? '#ffffff' : esNoIniciada ? '#343a40' : '#2c2c2c',
                    fontWeight: esFinalizada ? 500 : 700,
                  }}
                >
                  {estadoTexto}
                </span>

                {/* Badge tipo de prueba */}
                {ev.idTipoEvaluacion && (() => {
                  return (
                    <span
                      className="badge mt-1"
                      style={{
                        fontSize: '0.55rem',
                        backgroundColor: '#21c1ff',
                        color: '#0a2c47',
                        fontWeight: 700,
                      }}
                    >
                      {getTipoEval(ev.idTipoEvaluacion).label}
                    </span>
                  );
                })()}

                {/* Tiempo estimado */}
                {Number(ev.tiempoLimite) > 0 && (
                  <span className="badge mt-1" style={{ fontSize: '0.55rem', backgroundColor: '#e9ecef', color: '#343a40', fontWeight: 700 }}>
                    {ev.tiempoLimite} min
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Instrucciones */}
      <div className="alert alert-info mb-4">
        <h6 className="mb-2 d-flex align-items-center gap-2">
          <FontAwesomeIcon icon="info-circle" className="me-3" style={{ marginRight: '0.85rem' }} />
          <span style={{ paddingLeft: '4px' }}>Instrucciones:</span>
        </h6>
        <ul className="mb-0 ps-3 small">
          <li>Asegúrese de tener una conexión estable a internet.</li>
          <li>No cierre ni refresque la ventana del navegador durante cada evaluación.</li>
          <li>Una vez iniciada cada prueba, responda todas las preguntas antes de continuar.</li>
        </ul>
      </div>

      <div className="text-center">
        <button className="btn btn-primary btn-lg px-5 shadow" onClick={onIniciar}>
          {currentEvalIdx === 0 ? 'Iniciar Evaluaciones' : 'Continuar'}
          <FontAwesomeIcon icon="play" className="ms-3" style={{ marginLeft: '0.75rem' }} />
        </button>
      </div>
    </div>
  );
};

// ── Modal: Tiempo agotado ────────────────────────────────────────────────────

const ModalTiempoAgotado = ({ nombreEval, esUltimaEval, onContinuar }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(10,10,20,0.65)',
      backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'timerFadeIn 0.3s ease',
    }}
  >
    <div
      className="bg-white rounded-4 text-center p-5"
      style={{ maxWidth: 440, width: '92%', boxShadow: '0 24px 64px rgba(0,0,0,0.35)', animation: 'timerSlideUp 0.4s cubic-bezier(.22,1,.36,1)' }}
    >
      <div
        className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4"
        style={{ width: 88, height: 88, background: 'linear-gradient(135deg,#ff6b6b,#ee5a24)', boxShadow: '0 12px 36px rgba(238,90,36,0.4)' }}
      >
        <FontAwesomeIcon icon="hourglass-end" size="2x" className="text-white" />
      </div>
      <h3 className="fw-bold mb-2" style={{ color: '#2d3436' }}>¡Tiempo agotado!</h3>
      <p className="text-muted mb-1" style={{ fontSize: '0.95rem' }}>El tiempo asignado para</p>
      <p className="fw-semibold mb-4" style={{ fontSize: '1rem', color: '#2d3436' }}>"{nombreEval}"</p>
      {!esUltimaEval && (
        <p className="text-muted small mb-4">Tus respuestas han sido registradas. Avanza a la siguiente evaluación.</p>
      )}
      <button
        className="btn btn-lg w-100 fw-semibold text-white"
        style={{ background: 'linear-gradient(90deg,#0d6efd,#6610f2)', border: 'none', borderRadius: 12, padding: '12px' }}
        onClick={onContinuar}
      >
        <FontAwesomeIcon icon={esUltimaEval ? 'trophy' : 'arrow-right'} className="me-2" />
        {esUltimaEval ? 'Ver mis resultados' : 'Continuar con la siguiente evaluación'}
      </button>
    </div>
  </div>
);

// ── Modal: Preguntas sin responder ───────────────────────────────────────────

const ModalPendientes = ({ pendientesLista, onCancelar, onConfirmar, puedeConfirmar = true }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(10,10,20,0.65)',
      backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'timerFadeIn 0.3s ease',
    }}
  >
    <div
      className="bg-white rounded-4 text-center"
      style={{ maxWidth: 480, width: '92%', boxShadow: '0 24px 64px rgba(0,0,0,0.35)', animation: 'timerSlideUp 0.4s cubic-bezier(.22,1,.36,1)', padding: '36px 40px 32px' }}
    >
      {/* Icono sin círculo */}
      <div className="mb-3" style={{ color: '#fd7e14', fontSize: '2.8rem', lineHeight: 1 }}>
        <FontAwesomeIcon icon="list-check" />
      </div>

      <h3 className="fw-bold mb-2" style={{ color: '#2d3436' }}>Preguntas sin responder</h3>
      <p className="text-muted mb-4" style={{ fontSize: '0.95rem' }}>
        Las siguientes preguntas quedaron sin respuesta:
      </p>

      {/* Badges con más espaciado */}
      <div
        className="d-flex flex-wrap justify-content-center mb-4"
        style={{ gap: '10px 12px', maxHeight: 140, overflowY: 'auto' }}
      >
        {pendientesLista.map((num) => (
          <span
            key={num}
            style={{
              background: 'linear-gradient(135deg,#fd7e14,#ffc107)',
              color: 'white',
              fontSize: '0.85rem',
              padding: '7px 16px',
              borderRadius: '999px',
              fontWeight: 700,
              display: 'inline-block',
            }}
          >
            Preg. {num}
          </span>
        ))}
      </div>

      <p className="text-muted small mb-5">
        {puedeConfirmar
          ? 'Puedes volver a responderlas o finalizar dejándolas en blanco.'
          : 'Debes responder todas las preguntas para poder finalizar la evaluación.'}
      </p>

      {/* Botones: izquierda / derecha */}
      <div className={`d-flex align-items-center ${puedeConfirmar ? 'justify-content-between' : 'justify-content-center'}`}>
        <button
          className="btn btn-outline-secondary fw-semibold d-flex align-items-center"
          style={{ borderRadius: 9, padding: '8px 20px', fontSize: '0.88rem', gap: '10px' }}
          onClick={onCancelar}
        >
          <FontAwesomeIcon icon="arrow-left" />
          <span>Volver</span>
        </button>
        {puedeConfirmar && (
          <button
            className="btn fw-semibold text-white d-flex align-items-center"
            style={{ background: 'linear-gradient(90deg,#0d6efd,#6610f2)', border: 'none', borderRadius: 9, padding: '8px 20px', fontSize: '0.88rem', gap: '10px' }}
            onClick={onConfirmar}
          >
            <FontAwesomeIcon icon="check-circle" />
            <span>Finalizar evaluación</span>
          </button>
        )}
      </div>
    </div>
  </div>
);

// ── Modal: Advertencia de navegación ────────────────────────────────────────

const ModalAdvertenciaNavegacion = ({ onCancelar, onConfirmar }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(10,10,20,0.65)',
      backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    <div
      className="bg-white rounded-4 text-center p-5"
      style={{ maxWidth: 440, width: '92%', boxShadow: '0 24px 64px rgba(0,0,0,0.35)' }}
    >
      <div
        className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4"
        style={{ width: 88, height: 88, background: 'linear-gradient(135deg,#fd7e14,#dc3545)', boxShadow: '0 12px 36px rgba(220,53,69,0.4)' }}
      >
        <FontAwesomeIcon icon="exclamation-triangle" size="2x" className="text-white" />
      </div>
      <h4 className="fw-bold mb-2" style={{ color: '#2d3436' }}>¿Abandonar la evaluación?</h4>
      <p className="text-muted mb-2" style={{ fontSize: '0.95rem' }}>
        Si sales ahora, <strong>perderás todo el progreso</strong> de la evaluación en curso.
      </p>
      <p className="text-danger fw-semibold small mb-4">No podrás volver a rendir esta prueba.</p>
      <div className="d-flex gap-3">
        <button
          className="btn btn-outline-secondary flex-fill fw-semibold"
          onClick={onCancelar}
        >
          <FontAwesomeIcon icon="arrow-left" className="me-2" />
          Continuar evaluación
        </button>
        <button
          className="btn btn-danger flex-fill fw-semibold"
          onClick={onConfirmar}
        >
          <FontAwesomeIcon icon="sign-out-alt" className="me-2" />
          Salir de todas formas
        </button>
      </div>
    </div>
  </div>
);

// ── Renderer del countdown (SVG ring + dígitos) ───────────────────────────────

const CountdownRenderer = ({ hours, minutes, seconds, total, completed }, limitMs) => {
  if (completed) return null;
  const pct       = limitMs > 0 ? Math.max(0, (total / limitMs) * 100) : 0;
  const isUrgent  = total <= 2 * 60 * 1000;
  const isWarning = total <= 5 * 60 * 1000;
  const baseGreen = '#28c76f';
  const color     = isUrgent ? '#dc3545' : isWarning ? '#fd7e14' : baseGreen;
  const dash      = ((pct / 100) * 100).toFixed(1); // r≈15.9 → circunferencia≈100

  return (
    <div className="d-flex align-items-center gap-4" title={`${minutes}m ${seconds}s restantes`}>
      <svg viewBox="0 0 36 36" style={{ width: 42, height: 42, transform: 'rotate(-90deg)', flexShrink: 0 }}>
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e6f8ef" strokeWidth="3.2" />
        <circle
          cx="18" cy="18" r="15.9" fill="none"
          stroke={color} strokeWidth="3.2"
          strokeLinecap="round"
          strokeDasharray={`${dash} 100`}
          style={{ transition: 'stroke-dasharray 0.9s linear, stroke 0.6s ease' }}
        />
      </svg>
      <div style={{ width: '20px' }} />
      <div className="d-flex align-items-center gap-3" style={{ paddingRight: '4px' }}>
        <span
          className={isUrgent ? 'timer-pulse' : ''}
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 800,
            fontSize: '1.1rem',
            color,
            letterSpacing: '0.05em',
            transition: 'color 0.5s',
          }}
        >
          {hours > 0 && `${String(hours).padStart(2, '0')}:`}
          {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
        </span>
        {isUrgent && (
          <span className="badge bg-danger timer-pulse" style={{ fontSize: '0.65rem', color: 'white' }}>¡Urgente!</span>
        )}
      </div>
    </div>
  );
};

// ── Sub-componente: Fase 3 – Todas las preguntas en vista scrollable ─────────

const FaseEvaluacion = ({
  evalActual, totalEvals, evalIdx,
  preguntasList, respuestas,
  onResponder, onFinalizar,
  postulanteData, fechaInicioRef, authTokenRef, tokenValue, fase,
  finalizadoRef, beaconRef,
}) => {
  const instrucciones = evalActual?.instrucciones;
  const tiempoLimite  = evalActual?.tiempoLimite;   // minutos
  const limitMs       = tiempoLimite > 0 ? tiempoLimite * 60 * 1000 : 0;
  const nombreEval    = evalActual?.nombre ?? evalActual?.nombreEvaluacion ?? `Evaluación ${evalIdx + 1}`;
  const esUltimaEval  = evalIdx >= totalEvals - 1;
  const [showResumenPendientes, setShowResumenPendientes] = useState(false);
  const [pendientesLista, setPendientesLista] = useState([]);

  // Persist timer end time in localStorage
  const [endTime, setEndTime] = useState(null);

  useEffect(() => {
    if (limitMs > 0) {
      setEndTime(Date.now() + limitMs);
    } else {
      setEndTime(null);
    }
  }, [limitMs, evalActual?.idEvaluacion]);

  const [tiempoAgotado, setTiempoAgotado] = useState(false);

  // Calcular respuestas completas
  const respuestasCompletas = preguntasList.filter(preg => {
    const idPregunta = preg.idEvaluacionPregunta ?? preg.idPregunta ?? preg.id;
    const esTexto = !Array.isArray(preg.opciones) || preg.opciones.length === 0;
    const respActual = respuestas[idPregunta];
    if (esTexto) {
      return (respActual?.respuestaTexto ?? '').trim().length > 0;
    } else {
      return respActual?.idOpcion != null;
    }
  }).length;

  const pendientes = preguntasList
    .map((preg, idx) => {
      const idPregunta = preg.idEvaluacionPregunta ?? preg.idPregunta ?? preg.id;
      const esTexto = !Array.isArray(preg.opciones) || preg.opciones.length === 0;
      const respActual = respuestas[idPregunta];
      const respondida = esTexto
        ? (respActual?.respuestaTexto ?? '').trim().length > 0
        : respActual?.idOpcion != null;
      return respondida ? null : (idx + 1);
    })
    .filter(Boolean);

  const permiteBlancos = [248802, 248803].includes(evalActual?.idTipoEvaluacion);

  const handleFinalizar = () => {
    if (finalizadoRef.current) return;
    finalizadoRef.current = true;
    onFinalizar();
  };

  const buildRespuestasPayload = () => {
    const idPostulacion = postulanteData?.idPostulacion;
    const idEvaluacion  = evalActual?.idEvaluacion;
    const fechaFin      = new Date().toISOString();

    // Incluir TODAS las preguntas de la evaluación activa, aunque no hayan sido respondidas.
    const respuestasPayload = preguntasList.map(preg => ({
      idEvaluacionPregunta: preg.idEvaluacionPregunta,
      idOpcionRespuesta:    respuestas[preg.idEvaluacionPregunta]?.idOpcion ?? null,
    }));

    return {
      idPostulacion,
      idEvaluacion,
      fechaInicio:       fechaInicioRef.current,
      fechaFinalizacion: fechaFin,
      respuestas:        respuestasPayload,
    };
  };

  const guardarRespuestasBeacon = () => {
    if (beaconRef.current || finalizadoRef.current) return;
    const payload = buildRespuestasPayload();
    if (!payload.idPostulacion || !payload.idEvaluacion) return;
    beaconRef.current = true;

    const base = process.env.REACT_APP_URL_API_BACKEND || '';
    const url  = `${base}${guardarRespuestaEvaluacionAPI()}`;
    const body = JSON.stringify(payload);

    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } else {
      // fire-and-forget; no await para no bloquear cierre
      executePostPublic(guardarRespuestaEvaluacionAPI(), payload, authTokenRef.current || tokenValue);
    }
  };

  const handleFinalizarClick = () => {
    if (finalizadoRef.current) return;
    if (pendientes.length > 0) {
      setPendientesLista(pendientes);
      setShowResumenPendientes(true);
      return;
    }
    handleFinalizar();
  };

  useEffect(() => {
    if (fase !== FASE.EVALUACION) return undefined;
    const handler = (evt) => {
      if (evt?.type === 'visibilitychange' && document.visibilityState !== 'hidden') return;
      guardarRespuestasBeacon();
    };
    window.addEventListener('beforeunload', handler);
    document.addEventListener('visibilitychange', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
      document.removeEventListener('visibilitychange', handler);
    };
  }, [fase, respuestas, evalActual, postulanteData]);

  if (preguntasList.length === 0) return (
    <div className="text-center py-5">
      <p className="text-muted">Esta evaluación no tiene preguntas disponibles.</p>
      <button className="btn btn-primary" onClick={handleFinalizar}>Continuar</button>
    </div>
  );

  const porcentajeRespuestas = Math.round((respuestasCompletas / preguntasList.length) * 100);
  const allRespuestasCompletas = respuestasCompletas === preguntasList.length;

  return (
    <>
      {/* ── Modal tiempo agotado ── */}
      {tiempoAgotado && (
        <ModalTiempoAgotado
          nombreEval={nombreEval}
          esUltimaEval={esUltimaEval}
          onContinuar={handleFinalizar}
        />
      )}

      {/* Modal de pendientes cuando se permiten blancos */}
      {showResumenPendientes && (
        <ModalPendientes
          pendientesLista={pendientesLista}
          puedeConfirmar={permiteBlancos}
          onCancelar={() => setShowResumenPendientes(false)}
          onConfirmar={() => {
            setShowResumenPendientes(false);
            handleFinalizar();
          }}
        />
      )}

      {/* ── Header flotante con progreso + timer (sticky) ── */}
      <div
        className="card mb-4"
        style={{
          position: 'sticky',
          top: 116,
          zIndex: 1035,
          background: '#fff',
          padding: '16px 20px 12px',
          border: '1px solid #dee2e6',
          borderRadius: '10px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
          <div>
            <h5 className="mb-1 fw-bold">{nombreEval}</h5>
            <small className="text-muted">Evaluación {evalIdx + 1} de {totalEvals}</small>
          </div>
          <div className="text-end">
            {limitMs > 0 && endTime && (
              <div className="mb-2 d-flex justify-content-end">
                <div
                  className="d-inline-flex align-items-center gap-4 px-3 py-2 shadow-sm"
                  style={{
                    background: 'linear-gradient(135deg, #e9fdf3 0%, #d5f7e7 100%)',
                    border: '1px solid rgba(40, 199, 111, 0.6)',
                    borderRadius: '999px',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <span className="fw-semibold" style={{ color: '#0b8a4a', marginRight: '0.75rem' }}>Tiempo restante</span>
                  <Countdown
                    date={endTime}
                    renderer={(props) => CountdownRenderer(props, limitMs)}
                    onComplete={() => {
                      setTiempoAgotado(true);
                      handleFinalizar();
                    }}
                  />
                </div>
              </div> 
            )}
            <small className="text-muted d-block">
              {respuestasCompletas} de {preguntasList.length} respondidas
            </small>
          </div>
        </div>

        {/* Barra de progreso visual */}
        <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
          <div
            className="progress-bar bg-success"
            style={{
              width: `${porcentajeRespuestas}%`,
              transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: '4px',
            }}
          />
        </div>
        <small className="text-muted mt-1 d-block text-end">{porcentajeRespuestas}% completado</small>
      </div>

      {/* Instrucciones iniciales */}
      {instrucciones && (
        <div
          className="alert alert-info mb-4 d-flex gap-3"
          style={{
            borderRadius: '8px',
            borderLeft: '4px solid #0dcaf0',
            backgroundColor: '#f0f8ff',
          }}
        >
          <FontAwesomeIcon icon="lightbulb" size="lg" className="text-info flex-shrink-0 mt-1" style={{ marginRight: '0.75rem' }} />
          <div className="small text-dark">
            <strong>Instrucciones:</strong>
            <div className="tiptap-render mt-1 mb-0" dangerouslySetInnerHTML={{ __html: instrucciones }} />
          </div>
        </div>
      )}

      {/* Preguntas en vista de columna única */}
      <div className="d-flex flex-column gap-6 mb-5">
        {preguntasList.map((pregunta, idx) => {
          const idPregunta = pregunta.idEvaluacionPregunta ?? pregunta.idPregunta ?? pregunta.id;
          const esTexto = !Array.isArray(pregunta.opciones) || pregunta.opciones.length === 0;
          const respActual = respuestas[idPregunta];
          const tieneRespuesta = esTexto
            ? (respActual?.respuestaTexto ?? '').trim().length > 0
            : respActual?.idOpcion != null;

          return (
            <PreguntaCard
              key={idPregunta}
              numero={idx + 1}
              pregunta={pregunta}
              esTexto={esTexto}
              respActual={respActual}
              tieneRespuesta={tieneRespuesta}
              tipoEval={evalActual?.idTipoEvaluacion}
              onResponder={(value, esTextoResp) => onResponder(idPregunta, value, esTextoResp)}
            />
          );
        })}
      </div>

      {/* Resumen y botón de envío */}
      <div
        className="card border-0 bg-gradient p-4 mb-4"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          color: 'white',
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-1 fw-bold">Resumen de respuestas</h6>
            <p className="mb-0">
              <strong>{respuestasCompletas} de {preguntasList.length}</strong> preguntas respondidas
            </p>
          </div>
          <div className="text-center">
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{porcentajeRespuestas}%</div>
            <small>Completado</small>
          </div>
        </div>
      </div>

      {/* Botón de finalización */}
      <div className="d-flex gap-4 justify-content-between align-items-center mt-3" style={{ flexWrap: 'wrap' }}>
        <button
          className="btn btn-lg"
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '1rem',
            fontWeight: '600',
            border: 'none',
          }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <FontAwesomeIcon icon="arrow-up" className="me-3" style={{ marginRight: '0.75rem' }} />
          Ir al inicio
        </button>
        <button
          className="btn btn-lg"
          style={{
            backgroundColor: '#28a745',
            color: 'white',
            borderRadius: '8px',
            padding: '12px 32px',
            fontSize: '1rem',
            fontWeight: '600',
            border: 'none',
            cursor: 'pointer',
            opacity: 1,
          }}
          disabled={finalizadoRef.current}
          onClick={handleFinalizarClick}
        >
          <FontAwesomeIcon icon="check-circle" className="me-3" style={{ marginRight: '0.75rem' }} />
          Finalizar Evaluación
        </button>
      </div>
    </>
  );
};

// ── Sub-componente: Tarjeta de pregunta individual ────────────────────────

const PreguntaCard = ({ numero, pregunta, esTexto, respActual, tieneRespuesta, tipoEval, onResponder }) => {
  const [expanded, setExpanded] = useState(true);

  const enunciado = pregunta.textoPregunta ?? pregunta.enunciado ?? pregunta.pregunta ?? pregunta.texto ?? '(sin enunciado)';

  return (
    <div
      className="card border-0 shadow-sm p-0 w-100"
      style={{
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: 'fadeInUp 0.5s ease-out forwards',
        animation: `fadeInUp 0.5s ease-out forwards`,
        animationDelay: `${(numero - 1) * 0.08}s`,
        marginBottom: '12px',
      }}
    >
      {/* Header de la pregunta */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          backgroundColor: tieneRespuesta ? '#d4edda' : '#f8f9fa',
          borderBottom: expanded ? '1px solid #dee2e6' : 'none',
          padding: '16px',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.2s',
        }}
      >
        <div className="d-flex align-items-center gap-3 flex-grow-1">
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: tieneRespuesta ? '#28a745' : '#007bff',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              flexShrink: 0,
            }}
          >
            {tieneRespuesta ? <FontAwesomeIcon icon="check" /> : numero}
          </div>
          <div className="flex-grow-1 min-width-0">
            <h6 className="mb-1 fw-bold" style={{ wordBreak: 'break-word' }}>
              Pregunta {numero}
            </h6>
          </div>
        </div>
        <FontAwesomeIcon
          icon={expanded ? 'chevron-up' : 'chevron-down'}
          className="text-muted ms-2"
          style={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(0deg)' : 'rotate(-180deg)' }}
        />
      </div>

      {/* Contenido expandible */}
      {expanded && (
        <div
          style={{
            padding: '20px',
            animation: 'slideDown 0.3s ease-out',
          }}
        >
          {/* Enunciado completo */}
          <div className="card my-0 mb-3 bg-light border-1 p-3">
            <div className="tiptap-render fw-bold" dangerouslySetInnerHTML={{ __html: enunciado }} />
          </div>

          {/* Respuesta: texto libre */}
          {esTexto && (
            <textarea
              className="form-control"
              rows={4}
              placeholder="Escriba su respuesta aquí…"
              value={respActual?.respuestaTexto ?? ''}
              onChange={e => onResponder(e.target.value, true)}
              style={{
                borderRadius: '8px',
                padding: '12px',
                fontSize: '0.95rem',
                border: '1px solid #dee2e6',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = '#007bff'}
              onBlur={e => e.target.style.borderColor = '#dee2e6'}
            />
          )}

          {/* Respuesta: opciones */}
          {!esTexto && (
            <div className="d-flex flex-column gap-3">
              {(pregunta.opciones ?? []).map((opcion, opIdx) => {
                const idOpcion = opcion.idOpcionRespuesta ?? opcion.idOpcion ?? opcion.id;
                const selected = respActual?.idOpcion === idOpcion;
                const tipoEvaluacion = tipoEval ?? pregunta.idTipoEvaluacion ?? pregunta.idTipo ?? null;
                const puedeDesmarcar = [248802, 248803].includes(tipoEvaluacion);
                return (
                  <div
                    key={idOpcion}
                    onClick={() => {
                      if (selected && puedeDesmarcar) {
                        onResponder(null, false); // desmarcar para conocimientos/otros
                      } else {
                        onResponder(idOpcion, false);
                      }
                    }}
                    style={{
                      padding: '16px 16px',
                      borderRadius: '8px',
                      border: selected ? '2px solid #007bff' : '1px solid #dee2e6',
                      backgroundColor: selected ? '#e7f3ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                    onMouseEnter={e => {
                      if (!selected) {
                        e.currentTarget.style.borderColor = '#007bff';
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!selected) {
                        e.currentTarget.style.borderColor = '#dee2e6';
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    <div
                      style={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        border: selected ? 'none' : '2px solid #dee2e6',
                        backgroundColor: selected ? '#007bff' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s',
                        flexShrink: 0,
                      }}
                    >
                      {selected && <FontAwesomeIcon icon="check" style={{ fontSize: '0.6rem', color: '#fff' }} />}
                    </div>
                    <div
                      className="tiptap-render mb-0"
                      style={{ flex: 1 }}
                      dangerouslySetInnerHTML={{ __html: opcion.textoOpcion ?? opcion.texto ?? opcion.descripcion ?? opcion.nombre ?? '' }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .timer-pulse {
          animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

export default EvaluacionesPostulante;

